import { EventType } from "@/analytics/analytics-events";
import { Delayed } from "@/components/common/delayed";
import { ThreeContextTracker } from "@/components/common/three-context/three-context";
import { ModeTransitionManager } from "@/components/r3f/utils/mode-transition-manager";
import { ErrorHandlersContext } from "@/errors/components/error-handling-context";
import { Mode, ModeNames } from "@/modes/mode";
import { useCurrentSceneIfAvailable } from "@/modes/mode-data-context";
import {
  OverlayElementsContext,
  useOverlayElementsInitialState,
} from "@/modes/overlay-elements-context";
import {
  selectModeIsLoadingData,
  selectModeName,
} from "@/store/mode-selectors";
import { setModeIsTransitioning } from "@/store/mode-slice";
import { selectActiveElement } from "@/store/selections-selectors";
import {
  ANNOTATION_SHEET_BAR_HEIGHT,
  useIsAnnotationSheetMinimized,
} from "@/tools/annotation-shapes/annotation-creation-sheet";
import { useIsAnnotationShapesEnabled } from "@/tools/annotation-shapes/annotation-shapes-store";
import { AnnotationCreationPanel } from "@/tools/annotation-shapes/annotation-shapes-ui";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectToolVisibility } from "@/store/ui/ui-selectors";
import {
  changeToolVisibility,
  ToolName,
  ToolVisibility,
} from "@/store/ui/ui-slice";
import { isIElementPhotogrammetryScan } from "@/types/ielement-extra-types";
import {
  CanvasOverlay,
  selectAncestor,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { ToastContext } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, sanitizeFileName } from "@faro-lotv/foundation";
import {
  isIElementAreaSection,
  isIElementRoomSection,
  isIElementSectionElsScan,
  isIElementSectionFlash,
  isIElementSectionFocusScan,
  type IElement,
} from "@faro-lotv/ielement-types";
import { Box, CircularProgress } from "@mui/material";
import { useContextBridge } from "@react-three/drei";
import { DateTime } from "luxon";
import { domToCanvas } from "modern-screenshot";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type RefObject,
} from "react";
import { ReactReduxContext } from "react-redux";
import { getMode } from "../../modes";
import {
  SceneEventsContext,
  useSceneEvents,
} from "../common/scene-events-context";
import { TransparencySettingsProvider } from "../common/transparency-sliders/transparency-settings-context";
import { UiOverlayContextProvider } from "../common/ui-overlay-provider";
import { PointerFeedback } from "../devtools/pointer-feedback";
import { AdaptiveResolutionCanvas } from "../r3f/canvas/adaptive-resolution-canvas";
import { HIDE_FROM_SCREENSHOTS_CLASS } from "../r3f/utils/hide-from-screenshots-class";
import { AllModesOverlay } from "./all-modes-overlay";

type ModeCanvasProps = {
  /** Name of the mode we want to render */
  modeName: ModeNames;
};

/** @returns a wrapper to bridge context from React to R3F */
function ContextBridge({ children }: PropsWithChildren<unknown>): JSX.Element {
  const Bridge = useContextBridge(
    ReactReduxContext,
    OverlayElementsContext,
    ErrorHandlersContext,
    ToastContext,
    SceneEventsContext,
  );
  return <Bridge>{children}</Bridge>;
}

/**
 * @returns A specialized canvas to render a mode scene and it's overlay
 */
export function ModeCanvas({ modeName }: ModeCanvasProps): JSX.Element | null {
  const mode = getMode(modeName);
  const isLoading = useAppSelector(selectModeIsLoadingData);

  const overlayElements = useOverlayElementsInitialState();

  const currentScene = useCurrentSceneIfAvailable();
  const isSheetMinimized = useIsAnnotationSheetMinimized();
  const hasShapes = useIsAnnotationShapesEnabled();

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        // On phones the annotation creation sheet is over the canvas
        position: "relative",
      }}
    >
      <TransparencySettingsProvider activeSheets={currentScene?.activeSheets}>
        <UiOverlayContextProvider>
          <Box
            component="div"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              overflow: "auto",
              position: "relative",
              // Keep the z-index of the canvas overlays, like annotation pins and cards, inside the canvas,
              // so the annotation creation sheet and panel next to it always stay on top of them
              isolation: hasShapes ? "isolate" : undefined,
              // Shrink the canvas above the bar of the minimized annotation creation sheet. A margin, not a padding,
              // since the canvas controls are positioned absolutely and would ignore a padding
              mb: isSheetMinimized
                ? `calc(${ANNOTATION_SHEET_BAR_HEIGHT}px + env(safe-area-inset-bottom))`
                : 0,
            }}
          >
            <OverlayElementsContext value={overlayElements}>
              <ModeComponent mode={mode} />
            </OverlayElementsContext>

            {isLoading && (
              <Delayed
                delay={500}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                }}
              >
                <CircularProgress size="100px" />
              </Delayed>
            )}

            {!mode.exclusive && currentScene && <AllModesOverlay />}
          </Box>
          <AnnotationCreationPanel />
        </UiOverlayContextProvider>
      </TransparencySettingsProvider>
    </Box>
  );
}

type ModeComponentProps = {
  /** Current mode active in the store */
  mode: Mode;
};

function ModeComponent({ mode }: ModeComponentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const canvas = useRef<HTMLCanvasElement>(null);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousMode, setPreviousMode] = useState<Mode>(mode);

  // Update state of the transitioning so that the required components can be aware of the animation is needed
  useEffect(() => {
    dispatch(setModeIsTransitioning(isTransitioning));
  }, [dispatch, isTransitioning]);

  /**
   * Force React to not render the JSX when coming from a previous mode and re-render with isTransitioning enabled.
   * This will make sure that the transition of the current mode (if available) is executed before switching to
   * the overlay.
   *
   * See more on how React behaves on state change outside of a hook:
   * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
   */
  if (mode !== previousMode) {
    setIsTransitioning(true);
    setPreviousMode(mode);
  }

  // Prevents transition animations from restarting due to change in dependencies
  const onTransitionEnd = useCallback(() => setIsTransitioning(false), []);

  useTakeScreenshot(canvas);

  return (
    <>
      <AdaptiveResolutionCanvas style={{ position: "absolute" }} ref={canvas}>
        <ThreeContextTracker />
        <PointerFeedback>
          <ContextBridge>
            <ModeTransitionManager
              mode={mode}
              onTransitionEnd={onTransitionEnd}
            />
          </ContextBridge>
        </PointerFeedback>
      </AdaptiveResolutionCanvas>

      {!isTransitioning && mode.ModeOverlay && (
        <CanvasOverlay
          canvas={canvas}
          sx={{ padding: mode.hasCustomCanvasStyle ? 0 : 2 }}
        >
          <mode.ModeOverlay />
        </CanvasOverlay>
      )}
    </>
  );
}

/**
 * How long the screenshot button stays disabled after the download was
 * triggered, so that the browser has time to open its save dialog before the
 * user can start another screenshot.
 */
const SAVE_DIALOG_DELAY = 500;

function useTakeScreenshot(canvas: RefObject<HTMLCanvasElement | null>): void {
  const { takeScreenshot } = useSceneEvents();

  const dispatch = useAppDispatch();

  const { getState } = useAppStore();

  const isSplitMode = useAppSelector(selectModeName) === "split";

  const downloadCanvasScreenshot = useCallback(async () => {
    if (!canvas.current) return;

    // Showing a spinner on the screenshot button and blocking further clicks
    // on it, as creating the screenshot takes a moment
    dispatch(
      changeToolVisibility({
        name: ToolName.screenshot,
        visibility: ToolVisibility.waiting,
      }),
    );

    try {
      // Tracking the screenshot event for analytics purposes
      Analytics.track(EventType.takeScreenshot);

      // Rendering the scene in the canvas, so the frame is available for the screenshot.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });

      // Creating aux canvas of same size as mode canvas
      const result = document.createElement("canvas");
      result.width = canvas.current.width;
      result.height = canvas.current.height;

      // Getting the 2D context to draw both the GPU content and the HTML overlay elements
      const context = result.getContext("2d");
      assert(context, "Failed to get 2D context from canvas");
      const htmlContainer = isSplitMode
        ? canvas.current.parentElement?.parentElement
        : canvas.current.parentElement;
      assert(
        htmlContainer,
        "While creating a screenshot, HTML container was not found. This is unexpected.",
      );

      // Drawing content of 3D scene
      context.drawImage(
        canvas.current,
        0,
        0,
        canvas.current.width,
        canvas.current.height,
      );

      // Drawing all HTML elements rendered in overlay on the canvas
      const theCanvas = await domToCanvas(htmlContainer, {
        backgroundColor: null,
        scale: 1,
        filter: (node) => !shouldHideFromScreenshot(node),
      });
      context.drawImage(
        theCanvas,
        0,
        0,
        canvas.current.width,
        canvas.current.height,
      );

      // Getting the canvas content as PNG image
      const data = result.toDataURL("image/png");

      // Naming the screenshot after the current location
      const state = getState();
      const element = selectAncestor(
        selectActiveElement(state),
        isIElementSuitableForScreenshotName,
      )(state);
      const locationName = element?.name ?? "screenshot";

      // Downloading the screenshot as a PNG file
      const link = document.createElement("a");
      link.href = data;
      link.download = sanitizeFileName(
        `${DateTime.now().toISODate().replaceAll("-", "_")}_${locationName}.png`,
      );
      link.click();

      // Removing the canvas from the DOM so it can be disposed
      result.remove();
      theCanvas.remove();

      // Keeping the button disabled for a moment, to give the browser's save
      // dialog time to open
      await new Promise((resolve) => setTimeout(resolve, SAVE_DIALOG_DELAY));
    } finally {
      // Only re-enabling the button if nothing else changed its visibility in
      // the meantime, e.g. the toolbar hiding it when the mode was left while
      // the screenshot was being created
      if (
        selectToolVisibility(getState())[ToolName.screenshot] ===
        ToolVisibility.waiting
      ) {
        dispatch(
          changeToolVisibility({
            name: ToolName.screenshot,
            visibility: ToolVisibility.visible,
          }),
        );
      }
    }
  }, [canvas, dispatch, getState, isSplitMode]);

  useTypedEvent(takeScreenshot, downloadCanvasScreenshot);
}

/**
 * Checks whether the given node should be left out when taking a screenshot.
 *
 * The 3D scene is drawn from the mode canvas directly, so all canvases are
 * omitted from the capture of the HTML overlay on top of it.
 *
 * @param node The node to check
 * @returns Whether the given node should be left out of the screenshot
 */
function shouldHideFromScreenshot(node: Node): boolean {
  return (
    node instanceof HTMLCanvasElement ||
    (node instanceof HTMLDivElement &&
      node.className.includes(HIDE_FROM_SCREENSHOTS_CLASS))
  );
}

/**
 *
 * @param element The iElement to check
 * @returns Whether the given is element is suitable to name the screenshot after.
 */
function isIElementSuitableForScreenshotName(
  element: IElement | undefined,
): boolean {
  if (!element) return false;
  return (
    isIElementRoomSection(element) ||
    isIElementAreaSection(element) ||
    isIElementSectionFocusScan(element) ||
    isIElementSectionElsScan(element) ||
    isIElementSectionFlash(element) ||
    isIElementPhotogrammetryScan(element)
  );
}
